import { cancelCrsInspection, type CrsInspection } from "@/import-inspection";
import { isOnline } from "@/utils";
import { proxy, ref } from "valtio";

export type UploadStatus =
    | { kind: "queued" }
    | { kind: "uploading" }
    | { kind: "completed"; assetId: string }
    | { kind: "failed"; message: string };

export type Upload = {
    tmp_id: number;
    progress: number;
    status: UploadStatus;
    file: File;
    inspection: CrsInspection | null;
    bounds: { minX: number; maxX: number; minY: number; maxY: number } | null;
};

export type RasterUpload = Upload & {
    prjSidecar: Upload | null;
    worldSidecar: Upload | null;
};

export type UploadFetch = (url: string, options?: RequestInit) => Promise<Response>;

const authenticatedFetch: UploadFetch = async (url, options) => {
    const { auth } = await import("@/state");
    return auth.fetch(url, options);
};

const uploadFetchers = new WeakMap<Upload, UploadFetch>();

export const UploadManager = {
    startFile(file: File, fetcher: UploadFetch = authenticatedFetch): Upload {
        return startUpload(makeUpload(file), fetcher);
    },

    startRaster(file: File, prj: Upload | null, world: Upload | null): RasterUpload {
        return startUpload(makeRasterUpload(file, prj, world));
    },

    retry(upload: Upload): void {
        upload.progress = 0;
        upload.status = { kind: "queued" };
        manager.enqueue(upload);
    },

    cancel(upload: Upload): void {
        cancelCrsInspection(upload);
        manager.cancel(upload.tmp_id);
    },
};

export function cancelUploads(uploads: readonly Upload[]) {
    for (const upload of uploads) UploadManager.cancel(upload);
}

export function retryFailedUploads(uploads: readonly Upload[]) {
    for (const upload of uploads) if (upload.status.kind === "failed") UploadManager.retry(upload);
}

export function uploadsReady(uploads: readonly { status: { kind: UploadStatus["kind"] } }[]) {
    return uploads.length > 0 && uploads.every((upload) => upload.status.kind === "completed");
}

export function combinedUploadState(
    uploads: readonly { status: { kind: UploadStatus["kind"] } }[],
): UploadStatus["kind"] {
    if (uploadsReady(uploads)) return "completed";
    if (uploads.some((upload) => upload.status.kind === "uploading")) return "uploading";
    return "queued";
}

// Files whose name is already staged are skipped.
export function startUploads(uploads: Upload[], files: readonly File[]): Upload[] {
    const started: Upload[] = [];
    for (const file of files) {
        if (uploads.some((upload) => upload.file.name === file.name)) continue;
        const upload = UploadManager.startFile(file);
        uploads.push(upload);
        started.push(upload);
    }
    return started;
}

export function removeUpload(uploads: Upload[], tmpId: number) {
    const index = uploads.findIndex((upload) => upload.tmp_id === tmpId);
    if (index < 0) return;
    UploadManager.cancel(uploads[index]);
    uploads.splice(index, 1);
}

const CONCURRENCY = 4;
const RETRY_DELAYS_SEC = [2, 5, 15, 30, 60, 120, 180];
const STALL_TIMEOUT_MS = 60_000;
const API_TIMEOUT_MS = 5 * 60_000;

let nextId = 0;

function startUpload<T extends Upload>(upload: T, fetcher: UploadFetch = authenticatedFetch): T {
    const proxied = proxy<T>(upload);
    uploadFetchers.set(proxied, fetcher);
    manager.enqueue(proxied);
    return proxied;
}

function makeUpload(file: File): Upload {
    return {
        tmp_id: ++nextId,
        progress: 0,
        status: { kind: "queued" },
        file: ref(file),
        inspection: null,
        bounds: null,
    };
}

function makeRasterUpload(file: File, prj: Upload | null, world: Upload | null): RasterUpload {
    return { ...makeUpload(file), prjSidecar: prj, worldSidecar: world };
}

// status === null means a network/transport failure (no HTTP response).
class HttpError extends Error {
    constructor(
        message: string,
        readonly status: number | null,
        readonly body: string = "",
    ) {
        super(message);
    }
}

// S3 returns 400 RequestTimeout when the client streams the body too slowly — retryable.
// (Distinguishable from a real 400 only by the response body.)
const isRetryable = (e: unknown) =>
    e instanceof HttpError &&
    (e.status === null ||
        e.status === 408 ||
        e.status === 429 ||
        e.status >= 500 ||
        (e.status === 400 && e.body.includes("RequestTimeout")) ||
        ((e.status === 400 || e.status === 403) &&
            (e.body.includes("<Code>ExpiredToken</Code>") ||
                e.body.includes("<Code>RequestExpired</Code>") ||
                (e.body.includes("<Code>AccessDenied</Code>") && e.body.includes("Request has expired")))));

type UploadResponse =
    | { upload_type: "single"; asset_id: string }
    | {
          upload_type: "multipart";
          asset_id: string;
          upload_id: string;
          parts: { part_number: number }[];
          part_size: number;
      };

class Manager {
    private activeFiles = 0;
    private fileQueue: Array<{ upload: Upload; ctl: AbortController }> = [];
    private active = 0;
    private queue: Array<() => void> = [];
    private tracked = new Map<number, AbortController>();

    private onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();

    enqueue(upload: Upload): void {
        if (this.tracked.has(upload.tmp_id)) return;
        if (upload.status.kind !== "queued") return;
        const ctl = new AbortController();
        if (this.tracked.size === 0) window.addEventListener("beforeunload", this.onBeforeUnload);
        this.tracked.set(upload.tmp_id, ctl);
        this.fileQueue.push({ upload, ctl });
        this.drainFiles();
    }

    cancel(tmpId: number): void {
        this.tracked.get(tmpId)?.abort();
        this.fileQueue = this.fileQueue.filter(({ upload }) => upload.tmp_id !== tmpId);
        this.untrack(tmpId);
    }

    private drainFiles(): void {
        while (this.activeFiles < CONCURRENCY) {
            const job = this.fileQueue.shift();
            if (!job) return;
            const { upload, ctl } = job;
            upload.status = { kind: "uploading" };
            this.activeFiles++;
            this.run(upload, ctl).finally(() => {
                if (this.tracked.get(upload.tmp_id) === ctl) {
                    this.untrack(upload.tmp_id);
                }
                this.activeFiles--;
                this.drainFiles();
            });
        }
    }

    private untrack(tmpId: number): void {
        this.tracked.delete(tmpId);
        if (this.tracked.size === 0) window.removeEventListener("beforeunload", this.onBeforeUnload);
    }

    private async run(upload: Upload, ctl: AbortController): Promise<void> {
        const signal = ctl.signal;
        const fetcher = uploadFetchers.get(upload) ?? authenticatedFetch;
        const fileSize = upload.file.size || 1; // avoid NaN progress for zero-byte files
        try {
            const data = await withRetry(signal, () =>
                requestUploadApi<UploadResponse>(fetcher, "POST", "/assets/upload", signal, {
                    mode: "auto",
                    name: upload.file.name,
                    content_length: upload.file.size,
                    client_modified_at: new Date(upload.file.lastModified).toISOString(),
                }),
            );
            if (data.upload_type === "single") {
                await this.slot(() => {
                    return withRetry(signal, async () =>
                        xhrPut(
                            await fetchUploadUrl(fetcher, `/assets/${data.asset_id}/upload-url`, signal),
                            upload.file,
                            signal,
                            (loaded) => {
                                upload.progress = loaded / fileSize;
                            },
                        ),
                    );
                });
                if (signal.aborted) return;
                await withRetry(signal, () =>
                    requestUploadApi(fetcher, "POST", `/assets/${data.asset_id}/complete`, signal),
                ).catch((e) => {
                    if (!signal.aborted) console.warn("completing single upload failed:", e);
                });
                if (signal.aborted) return;
                upload.progress = 1;
                upload.status = { kind: "completed", assetId: data.asset_id };
                return;
            }

            let totalLoaded = 0;
            const partBytes = new Map<number, number>();
            const onPart = (n: number, loaded: number) => {
                totalLoaded += loaded - (partBytes.get(n) ?? 0);
                partBytes.set(n, loaded);
                upload.progress = totalLoaded / fileSize;
            };

            const completed = await Promise.all(
                data.parts.map(({ part_number }) =>
                    this.slot(async () => {
                        const start = (part_number - 1) * data.part_size;
                        const urlPath = `/assets/${data.asset_id}/upload-url?part_number=${part_number}`;
                        const etag = await withRetry(signal, async () =>
                            xhrPut(
                                await fetchUploadUrl(fetcher, urlPath, signal),
                                upload.file.slice(start, start + data.part_size),
                                signal,
                                (loaded) => onPart(part_number, loaded),
                            ),
                        );
                        if (!etag) throw new Error(`No ETag returned for part ${part_number}`);
                        return { part_number, etag };
                    }),
                ),
            );

            if (signal.aborted) return;
            await withRetry(signal, () =>
                requestUploadApi(fetcher, "POST", `/assets/${data.asset_id}/complete`, signal, {
                    upload_id: data.upload_id,
                    parts: completed,
                }),
            );
            if (signal.aborted) return;
            upload.progress = 1;
            upload.status = { kind: "completed", assetId: data.asset_id };
        } catch (e) {
            if (signal.aborted) return;
            ctl.abort(); // stop sibling part jobs still in flight
            console.error("upload failed:", e);
            upload.status = { kind: "failed", message: "Upload failed" };
        }
    }

    private slot<T>(job: () => Promise<T>): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            this.queue.push(() => {
                job()
                    .then(resolve, reject)
                    .finally(() => {
                        this.active--;
                        this.drain();
                    });
            });
            this.drain();
        });
    }

    private drain(): void {
        while (this.active < CONCURRENCY) {
            const job = this.queue.shift();
            if (!job) return;
            this.active++;
            job();
        }
    }
}

const manager = new Manager();

async function requestUploadApi<T>(
    fetcher: UploadFetch,
    method: "GET" | "POST",
    path: string,
    signal: AbortSignal,
    body?: unknown,
): Promise<T> {
    let res: Response;
    try {
        res = await fetcher(`/api${path}`, {
            method,
            headers: body === undefined ? undefined : { "Content-Type": "application/json" },
            body: body === undefined ? undefined : JSON.stringify(body),
            signal: AbortSignal.any([signal, AbortSignal.timeout(API_TIMEOUT_MS)]),
        });
    } catch (e) {
        if (signal.aborted) throw e;
        throw new HttpError(`${path}: network error`, null);
    }
    if (!res.ok) throw new HttpError(`${path}: HTTP ${res.status}`, res.status, await res.text());
    return (await res.json()) as T;
}

// Presigned URLs die with the server's temporary credentials, which can be long before their
// nominal expiry, so a URL is fetched right before each PUT attempt.
async function fetchUploadUrl(fetcher: UploadFetch, path: string, signal: AbortSignal): Promise<string> {
    return (await requestUploadApi<{ upload_url: string }>(fetcher, "GET", path, signal)).upload_url;
}

async function withRetry<T>(signal: AbortSignal, fn: () => Promise<T>): Promise<T> {
    for (let attempt = 0; ; attempt++) {
        await waitUntilOnline(signal);
        try {
            return await fn();
        } catch (e) {
            if (signal.aborted || !isRetryable(e) || attempt >= RETRY_DELAYS_SEC.length) throw e;
            await sleepWithAbort(RETRY_DELAYS_SEC[attempt] * 1000, signal);
        }
    }
}

const abortError = () => new DOMException("aborted", "AbortError");

// Browsers report onLine=false only when definitely offline; true is no guarantee of
// connectivity, so this is a best-effort pause rather than a substitute for retries.
function waitUntilOnline(signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal.aborted) return reject(abortError());
        if (isOnline()) return resolve();

        const cleanup = () => {
            window.removeEventListener("online", onOnline);
            signal.removeEventListener("abort", onAbort);
        };
        const onOnline = () => {
            cleanup();
            resolve();
        };
        const onAbort = () => {
            cleanup();
            reject(abortError());
        };
        window.addEventListener("online", onOnline, { once: true });
        signal.addEventListener("abort", onAbort, { once: true });
    });
}

function sleepWithAbort(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal.aborted) return reject(abortError());

        const timeout = setTimeout(() => {
            signal.removeEventListener("abort", onAbort);
            resolve();
        }, ms);

        const onAbort = () => {
            clearTimeout(timeout);
            reject(abortError());
        };

        signal.addEventListener("abort", onAbort, { once: true });
    });
}

// Resolves with the response's ETag header (needed for multipart parts; single
// uploads ignore it).
function xhrPut(
    url: string,
    body: Blob,
    signal: AbortSignal,
    onProgress: (loaded: number) => void,
): Promise<string | null> {
    return new Promise((resolve, reject) => {
        if (signal.aborted) return reject(abortError());
        const xhr = new XMLHttpRequest();

        // Watchdog: abort if no bytes move for STALL_TIMEOUT_MS. Armed at send (covers
        // connection setup), re-armed on each progress event (covers mid-body stalls),
        // and left running after the last one (covers a response that never arrives).
        let stalled = false;
        let stallTimer: ReturnType<typeof setTimeout>;
        const armStallTimer = () => {
            clearTimeout(stallTimer);
            stallTimer = setTimeout(() => {
                stalled = true;
                xhr.abort();
            }, STALL_TIMEOUT_MS);
        };

        const onAbort = () => xhr.abort();
        signal.addEventListener("abort", onAbort);
        const done = () => {
            clearTimeout(stallTimer);
            signal.removeEventListener("abort", onAbort);
        };

        xhr.upload.onprogress = (e) => {
            armStallTimer();
            if (e.lengthComputable) onProgress(e.loaded);
        };
        xhr.onload = () => {
            done();
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(xhr.getResponseHeader("ETag"));
            } else {
                reject(new HttpError(xhr.statusText || `HTTP ${xhr.status}`, xhr.status, xhr.responseText));
            }
        };
        xhr.onerror = () => {
            done();
            reject(new HttpError("Network error", null));
        };
        xhr.onabort = () => {
            done();
            reject(stalled ? new HttpError("Upload stalled", null) : abortError());
        };
        xhr.open("PUT", url);
        armStallTimer();
        onProgress(0); // discard bytes counted by a failed previous attempt
        xhr.send(body);
    });
}
